import type { Settings } from './settingsTypes';

const GUTTING_WASTE = 0.17;

/* Old, should be deleted */
const settings = {
    guttingWaste: GUTTING_WASTE,
    slaughteredFactor: 1 - GUTTING_WASTE,
    weightAverageDayDiffThreshold: 5,
    primaryColor: '#5bb784',
    secondaryColor: '#547fa2',
    orangeColor: '#FF7E0E',
    blueColor: '#00E6F0',
    lightBlueColor: '#66CCEE',
    darkGrayColor: '#CCCCCC',
    purpleColor: '#800080',
    warningColor: '#C65461',
    disabledColor: '#E0E0E0',
    abnormalPitchThreshold: 10,
    abnormalRollThreshold: 10,
    abnormalMovementScoreThreshold: 0.2,
    dayFromHour: 7,
    nightFromHour: 18,
    swimspeed: {
        low_speed_threshold: 0.2,
        medium_speed_threshold: 1.5,
        high_speed_threshold: 2.0,
    },
    woundGrowthLimit: 0.025,
};

/**
 * The data palette, as its light values. Charts never paint these directly in the dark theme:
 * they pass each colour through `ChartTheme.color` (components/charts/theme), which swaps in
 * the dark lift for the few that need one (`PALETTE_LIFT_TOKENS`, values in
 * styles/tokens/_chart.scss). Keep each colour a 6-digit hex so the lift can find it.
 */
export enum OptoColors {
    Riverside = '#4579A3',
    OceanGreen = '#5BB784',
    Red = '#C65461',
    Yellow = '#FFD479',
    Lavender = '#696999',
    DeepCyan = '#559DA3',
    Terracotta = '#E87166',
    Olivine = '#B6C377',
    Purple = '#8D6A99',
    Orange = '#F4A46F',
    Pink = '#CE7997',
    LeafGreen = '#80B580',
    White = '#FFFFFF',
}

export const GraphColorsInOrder = [
    OptoColors.Riverside,
    OptoColors.OceanGreen,
    OptoColors.Red,
    OptoColors.Yellow,
    OptoColors.Lavender,
    OptoColors.DeepCyan,
    OptoColors.Terracotta,
    OptoColors.Olivine,
    OptoColors.Purple,
    OptoColors.Orange,
    OptoColors.Pink,
    OptoColors.LeafGreen,
];

const graphSettings: Settings = {
    guttingWaste: GUTTING_WASTE,
    slaughteredFactor: 1 - GUTTING_WASTE,
    weightAverageDayDiffThreshold: 5,
    qualityReportColors: {
        notSuperior: '#32714e',
        superior: '#8ECDAA',
        maturation: '#DB9A4E',
    },
    SceneLabelTagsColors: {
        optimal: '#5BB784',
        singleFish: '#559DA3',
        farAway: '#4579A3',
        tooClose: '#696999',
        blocked: '#C65461',
        badAngle: '#E87166',
        blurry: '#F4A46F',
    },
    fishHealthColors: {
        noWound: OptoColors.OceanGreen,
        active: OptoColors.Red,
        healing: '#4579A3',
        large: '#d8454b',
        medium: '#f58c55',
        small: '#f9c975',
        singleWound: '#5BB784',
        multipleWounds: '#547FA2',
        temperature: '#EAF0FE',
    },
    maturationColors: {
        no: '#5bb784',
        started: '#f8d0d6',
        full: '#ff7c6a',
    },
    daytagColors: {
        all: OptoColors.OceanGreen,
        day: OptoColors.Orange,
        night: OptoColors.Purple,
        trend_line: OptoColors.Riverside,
    },
    minMaxAvgColors: {
        min: '#547fa2',
        max: '#141920',
        avg: '#5bb784',
    },
    heightLengthColors: {
        height: '#5bb784',
        length: '#547fa2',
    },
    colors: {
        primary: '#5bb784',
        secondary: '#547fa2',
        tertiery: '#00E6F0',
        highlight: '#FF7E0E',
        light: '#66CCEE',
        dark: '#4A485F',
        indigo: '#6776b9',
    },
    graphColors: {
        fishCount: '#5BB784',
        adult_female: OptoColors.Pink,
        mobile: OptoColors.LeafGreen,
        caligus: '#4579A3',
        temperature: '#9BBBD5',
        band: '#D4E2ED',
        weightDistribution: '#3D5F7D',
        dayMarker: '#1e6e96',
        woundTemperature: '#5B8EB6',
    },
    statusColors: {
        positive: '#5bb784',
        negative: '#dd0004',
        neutral: '#547fa2',
    },
    alertColors: {
        danger: '#ff1f09',
        warning: '#ff7e0e',
        success: '#5bb784',
        neutral: '#547fa2',
    },
    comparingPensColors: GraphColorsInOrder,
    designSystemColors: [
        '#000000',
        '#FFFFFF',
        '#4579A3',
        '#559DA3',
        '#5BB784',
        '#696999',
        '#80B580',
        '#8D6A99',
        '#B6C377',
        '#C65461',
        '#CE7997',
        '#E87166',
        '#F4A46F',
        '#FFD479',
    ],

    designSystemShades: [
        '#FFFFFF',
        '#000000',
        '#14172D',
        '#262F54',
        '#5D6182',
        '#74799A',
        '#8B91B1',
        '#A2A9C8',
        '#B9C0DF',
        '#D0D4E9',
        '#E9EBF5',
        '#F8F8FC',
        '#FFFFFF',
    ],
    abnormalPitchThreshold: 10,
    abnormalRollThreshold: 10,
    abnormalMovementScoreThreshold: 0.2,
    dayFromHour: 7,
    nightFromHour: 18,
    fishTypeName: {
        salmon: '#5bb784',
        rainbow_trout: '#274f8c',
        trout: '#274f8c',
        // Placeholder until design picks one; distinct from the other two.
        coho: '#FF7E0E',
    },
    laksvel: {
        score_1: '#FFD479',
        score_2: '#F4A46F',
        score_3: '#E87166',
        info_icon: '#547fa2',
    },
};

/*
const LOW_SPEED_THRESHOLD = 0.5;
const MEDIUM_SPEED_THRESHOLD = 1.5;
const HIGH_SPEED_THRESHOLD = 2.0;
*/

function hexToRGBA(hex: string, alpha = 1): string {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * `graphSettings.alertColors` as theme tokens, for status icons drawn on themed surfaces. The
 * chart code keeps the hex values, since the chart theme lifts those itself.
 */
export const alertIconColors = {
    danger: 'var(--color-status-icon-danger)',
    warning: 'var(--color-status-icon-warning)',
    success: 'var(--color-status-icon-success)',
    neutral: 'var(--color-status-icon-neutral)',
} as const;

export default settings;
export { graphSettings, hexToRGBA, settings };
